import { useCallback, useState } from 'react';
import type { Evenement } from '@qc/db';
import { supabase } from '../supabase.js';
import { Knop } from '@qc/ui';
import { Apparaten } from '../beheer/Apparaten.js';
import { Prijsmodel } from '../beheer/Prijsmodel.js';
import { Afrekening } from './Afrekening.js';
import { Afsluitknop } from './Afsluitknop.js';
import { Ingrijpen } from './Ingrijpen.js';
import { KramenBeheer } from './KramenBeheer.js';
import { LiveCijfers } from './LiveCijfers.js';
import { MenuGoedkeuring } from './MenuGoedkeuring.js';
import { useEvenement } from './useEvenement.js';

export interface EvenementSchermProps {
  evenement: Evenement;
  /** De afrekening is alleen voor de beheerder; die betaalt uit. */
  isAdmin: boolean;
  onTerug: () => void;
}

type Tab =
  | 'live'
  | 'kramen'
  | 'menus'
  | 'ingrijpen'
  | 'afsluiten'
  | 'afrekening'
  | 'prijsmodel'
  | 'apparatuur';

const TABS: { sleutel: Tab; naam: string }[] = [
  { sleutel: 'live', naam: 'Live' },
  { sleutel: 'kramen', naam: 'Kramen' },
  { sleutel: 'menus', naam: "Menu's" },
  { sleutel: 'ingrijpen', naam: 'Ingrijpen' },
  { sleutel: 'afsluiten', naam: 'Afsluiten' },
];

const ADMIN_TABS: { sleutel: Tab; naam: string }[] = [
  { sleutel: 'afrekening', naam: 'Afrekening' },
  { sleutel: 'prijsmodel', naam: 'Prijsmodel' },
  { sleutel: 'apparatuur', naam: 'Apparatuur' },
];

export function EvenementScherm({ evenement, isAdmin, onTerug }: EvenementSchermProps) {
  // Het evenement staat ook lokaal: wijzigt de beheerder het prijsmodel, dan
  // moeten de afrekening en de marge meteen met de nieuwe tarieven rekenen.
  const [huidig, setHuidig] = useState<Evenement>(evenement);
  const beeld = useEvenement(huidig);

  const haalEvenementOpnieuw = useCallback(async () => {
    const { data } = await supabase.from('evenementen').select('*').eq('id', evenement.id).limit(1);
    if (data?.[0] !== undefined) setHuidig(data[0]);
    await beeld.ververs();
  }, [beeld, evenement.id]);
  const [tab, setTab] = useState<Tab>('live');
  const [afgesloten, setAfgesloten] = useState(evenement.status === 'afgesloten');

  const teGoedkeuren = beeld.menusTerGoedkeuring.length;

  return (
    <div className="mx-auto min-h-full max-w-5xl p-8">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <button type="button" onClick={onTerug} className="text-tekst-zacht active:text-tekst">
            ← Alle evenementen
          </button>
          <h1 className="truncate text-3xl font-bold">{huidig.naam}</h1>
        </div>
        <Knop nadruk="stil" onClick={() => void beeld.ververs()} className="text-base">
          Nu verversen
        </Knop>
      </header>

      <nav className="mb-6 flex gap-2 overflow-x-auto">
        {(isAdmin ? [...TABS, ...ADMIN_TABS] : TABS).map((item) => (
          <button
            key={item.sleutel}
            type="button"
            onClick={() => setTab(item.sleutel)}
            className={`min-h-12 shrink-0 rounded-tegel px-5 text-lg font-semibold ${
              item.sleutel === tab
                ? 'bg-accent text-accent-tekst'
                : 'border border-rand bg-vlak text-tekst'
            }`}
          >
            {item.naam}
            {item.sleutel === 'menus' && teGoedkeuren > 0 ? ` (${teGoedkeuren})` : ''}
          </button>
        ))}
      </nav>

      {beeld.fout !== null && <p className="mb-4 text-lg text-fout">{beeld.fout}</p>}
      {beeld.laden && <p className="text-tekst-zacht">Bezig met laden…</p>}

      {!beeld.laden && tab === 'live' && (
        <LiveCijfers
          cijfers={beeld.cijfers}
          bijgewerktOp={beeld.bijgewerktOp}
          waarschuwingen={beeld.waarschuwingen}
        />
      )}
      {!beeld.laden && tab === 'kramen' && <KramenBeheer beeld={beeld} />}
      {!beeld.laden && tab === 'menus' && <MenuGoedkeuring beeld={beeld} />}
      {!beeld.laden && tab === 'ingrijpen' && <Ingrijpen beeld={beeld} />}
      {tab === 'afrekening' && isAdmin && <Afrekening evenement={huidig} />}
      {tab === 'prijsmodel' && isAdmin && (
        <Prijsmodel
          key={JSON.stringify(huidig)}
          evenement={huidig}
          onOpgeslagen={haalEvenementOpnieuw}
        />
      )}
      {tab === 'apparatuur' && isAdmin && <Apparaten evenement={huidig} />}
      {!beeld.laden && tab === 'afsluiten' && (
        <Afsluitknop
          beeld={beeld}
          alAfgesloten={afgesloten}
          onAfgesloten={() => setAfgesloten(true)}
        />
      )}
    </div>
  );
}
